/**
 * Makes Cmd+Z restore a just-rejected AI proposal, the reject-side counterpart
 * to accept-undo.
 *
 * WHY THIS CAN'T RIDE THE NORMAL UNDO STACK. Accepting a proposal inserts
 * content — a real document change the Yjs/Liveblocks UndoManager captures, so
 * Cmd+Z reverses it and the content-derived reconciler flips the record back to
 * pending on its own (see useAISuggestionHydration's reconciler +
 * isUndoTransaction). Rejecting a proposal only removes its decoration
 * (`rejectAIProposal` sets plugin meta, no doc change), so the UndoManager has
 * nothing to capture and Cmd+Z would skip straight past it to the previous
 * edit. This extension supplies the missing step: it records each reject and,
 * on Cmd+Z, re-pends the most recent one instead of delegating to the
 * UndoManager.
 *
 * THE CLEAN SPLIT (so accept-undo is untouched). The keymap handler swallows
 * Cmd+Z ONLY when there is an un-disarmed reject to restore. For everything
 * else — accept-undo, ordinary text undo, an empty stack — it returns false and
 * the key falls through to the Liveblocks collaboration extension's `undo`
 * (this extension's `priority` is set above Liveblocks' so its handler runs
 * first in the keymap chain). So accept's text reversal stays with the real
 * UndoManager; we never double-handle it.
 *
 * DISARM-ON-EDIT LATCH. A reject is only undoable while nothing else has
 * changed the document since it happened. The moment the clinician makes any
 * forward edit, the pending stack is cleared — so reject → type → Cmd+Z undoes
 * the typing (via the UndoManager), it does not resurrect a suggestion the
 * clinician has since moved past. This mirrors the accept-fix latch, where a
 * forward edit after accept stops the record from flipping back to pending.
 *
 * FUSED REJECT-AND-APPLY (`withDocUndo`). The choice node view's other-option
 * click rejects the pending suggestion in the SAME transaction that applies the
 * clicked value. That reject must be recorded AFTER the fused transaction
 * dispatches (recording inside it would be immediately wiped by the disarm
 * latch — the apply IS a doc change), and its undo must restore BOTH halves in
 * one key press: the interceptor first delegates a document undo to the real
 * UndoManager (reverting the selection), then re-pends the proposal against the
 * restored doc. Redo symmetrically re-applies the document change and
 * re-rejects.
 *
 * REDO. Cmd+Shift+Z (and Cmd+Y) re-reject the proposal an interceptor-undo just
 * restored, popping the redo stack the same LIFO way. Like undo, redo only
 * swallows the key when it has something to do; otherwise it delegates — with
 * one exception, an undone bulk decision, which swallows the key to do NOTHING
 * (see `BatchEntry`).
 *
 * BULK DECISIONS (`recordRejectedAIProposalBatch` /
 * `recordAcceptedAIProposalBatch`). "Reject all" and "Accept all" each push a
 * single entry standing for the entire sweep, whose undo is one host-supplied
 * restore covering every surface the sweep reached — records, questionnaire
 * atoms, and the decorations it tore down. They live here because Cmd+Z is
 * already arbitrated: a sweep spans surfaces (inline proposals, records that
 * never placed, care-plan questionnaire atoms) but only one of them owns the
 * key while the clinician's focus is in the editor, and one press must bring
 * all of them back. An accept sweep additionally CHANGES THE DOCUMENT, so its
 * entry takes the same `withDocUndo` treatment as a fused reject-and-apply.
 * See `BatchEntry`.
 */
import { type Editor, Extension } from '@tiptap/core'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import type { AIProposal } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { isLocalForwardEdit } from '#src/components/rich-text-editor/transactionOrigin.ts'

export interface AIProposalUndoOptions {
  /**
   * Persist the status flip that accompanies an interceptor undo/redo, so the
   * staging record matches what the decoration now shows. Undo re-pends (status
   * back to 'pending'); redo re-rejects (status 'rejected'). Absent → no
   * persistence (non-note editors), decoration-only.
   */
  onRepend?: (id: string) => void
  onReReject?: (id: string) => void
}

interface SingleRejectEntry {
  kind: 'single'
  proposal: AIProposal
  /**
   * The reject was fused with a document change (reject-and-apply): its undo
   * must also delegate a document undo to the real UndoManager, and its redo a
   * document redo, so both halves travel as one step from the user's view.
   */
  withDocUndo?: boolean
}

/* A whole "Reject all" or "Accept all" as ONE entry, so recovering from a
 * misclick costs one key press however many suggestions the sweep covered.
 *
 * Its payload is a restore thunk rather than a list of proposals because the
 * sweep reaches past this editor: besides the painted decorations it decides
 * the records that never placed and — on the care plan — every still-undecided
 * questionnaire atom. Only the first of those lives in the plugin state, so the
 * host, which holds all three, restores all three — including repainting the
 * decorations it snapshotted before clearing them.
 *
 * `withDocUndo` is what makes an ACCEPT sweep one press rather than two. Its
 * inline half inserted content, grouped into a single UndoManager step, which
 * only the UndoManager can reverse; its questionnaire half is a compensating
 * FHIR patch, which only the host can. So the entry does what a fused
 * reject-and-apply does: delegate the document undo first, then run the
 * restore. A reject sweep changes no content and needs neither.
 *
 * The batch is undoable but NOT redoable: the inline "Undo rejection of all"
 * button it shares its armed state with has never offered a redo either, and
 * re-deciding would have to re-derive placed ids against decorations that
 * hydration is still busy re-painting. Pressing "Accept all" again is the way
 * forward from an undone sweep.
 *
 * WHICH IS WHY ITS UNDO ARMS `batchUndone`. "Not redoable" cannot mean "let the
 * key fall through". An accept sweep's document half is still sitting in the
 * collaboration UndoManager's own redo stack, so a delegated Cmd+Shift+Z
 * replays the narrative edits while the questionnaire answers stay reversed —
 * a note asserting in prose what the answers no longer say, with nothing on
 * screen saying the two halves have parted. So the undo leaves a latch
 * (`batchUndone`) that makes redo inert — swallowed, not delegated — until a
 * forward edit puts the sweep out of the note history's reach. A reject sweep
 * changes no content and would be harmless either way; it arms the latch too,
 * so the rule is one the reader can state without knowing which sweep ran.
 */
interface BatchEntry {
  kind: 'batch'
  restore: () => void
  withDocUndo?: boolean
}

type RejectEntry = SingleRejectEntry | BatchEntry

export interface AIProposalUndoStorage {
  /** Rejects available to re-pend, most-recent last (LIFO). */
  undoStack: RejectEntry[]
  /** Interceptor-undone rejects available to re-reject, most-recent last. */
  redoStack: SingleRejectEntry[]
  /**
   * True only while the extension is itself dispatching a re-pend / re-reject,
   * so the doc-watch plugin doesn't mistake our own transaction for a forward
   * edit and disarm the stacks.
   */
  applying: boolean
  // True from the moment a batch entry is undone until a local forward edit,
  // and nothing else — those are exactly the transactions that discard the
  // note's own redo branch, and so the only ones that put the undone sweep out
  // of its reach. While it is set, redo does nothing (see `BatchEntry`); a
  // decision of ours still owns its redo, because the redo stack is consulted
  // first.
  batchUndone: boolean
}

const AI_PROPOSAL_UNDO_PLUGIN_KEY = new PluginKey('aiProposalUndoLatch')

export const AIProposalUndoExtension = Extension.create<
  AIProposalUndoOptions,
  AIProposalUndoStorage
>({
  name: 'aiProposalUndo',

  /* Must beat the Liveblocks collaboration extension's keymap so our Mod-z runs
   * first and can swallow the key for a reject re-pend (returning false
   * otherwise falls through to its `undo`). Tiptap registers keymap plugins in
   * descending-priority order and the first to handle a key wins; the
   * Liveblocks collaboration extension sets `priority: 1000`, so we sit just
   * above it. */
  priority: 1001,

  addOptions() {
    return {}
  },

  addStorage() {
    return {
      undoStack: [],
      redoStack: [],
      applying: false,
      batchUndone: false,
    }
  },

  addCommands() {
    /* One sweep back, from either affordance — the Cmd+Z that finds the entry
     * on top of the stack, or the bar's button that goes looking for it. */
    const undoBatch = (
      entry: BatchEntry,
      editor: Editor,
      tr: Transaction
    ): void => {
      /* An accept sweep's inline half is a document change; take it back
       * FIRST, so the host's compensating writes land against the note the
       * clinician is left looking at. Same stale-tr suppression as the fused
       * single entry: the nested undo dispatches its own transaction. */
      if (entry.withDocUndo) {
        tr.setMeta('preventDispatch', true)
        this.storage.applying = true
        editor.commands.undo?.()
        this.storage.applying = false
      }
      // The rest of the sweep comes back through the host, which owns every
      // surface it covered — decorated, unplaced, and questionnaire atoms
      // alike. The repaint of the inline decorations rides along inside
      // `restore` (the host holds the snapshot the sweep un-painted);
      // re-pending a record does not repaint it, because the hydration paint
      // is a one-shot the re-pend never re-arms.
      entry.restore()
      /* Arm the redo swallow LAST: `restore` runs un-suppressed, so any
       * document change it made would otherwise reach the disarm plugin and
       * clear the latch we had just set. From here Cmd+Shift+Z is inert until
       * a forward edit — see `BatchEntry`. */
      this.storage.batchUndone = true
    }

    return {
      recordRejectedAIProposals:
        (proposals: AIProposal[], options?: { withDocUndo?: boolean }) =>
        ({ dispatch }) => {
          // Guard on `dispatch`: Tiptap runs command bodies with no dispatch
          // during `can()` dry-runs, and we must not mutate the stacks then.
          if (dispatch && proposals.length > 0) {
            for (const proposal of proposals) {
              this.storage.undoStack.push({
                kind: 'single',
                proposal,
                withDocUndo: options?.withDocUndo,
              })
            }
            // A fresh reject branches off any redo history, exactly like a
            // normal edit after an undo.
            this.storage.redoStack = []
          }
          return true
        },

      recordRejectedAIProposalBatch:
        (restore: () => void) =>
        ({ dispatch }) => {
          if (dispatch) {
            this.storage.undoStack.push({ kind: 'batch', restore })
            this.storage.redoStack = []
          }
          return true
        },

      recordAcceptedAIProposalBatch:
        (restore: () => void, options?: { withDocUndo?: boolean }) =>
        ({ dispatch }) => {
          if (dispatch) {
            this.storage.undoStack.push({
              kind: 'batch',
              restore,
              withDocUndo: options?.withDocUndo,
            })
            this.storage.redoStack = []
          }
          return true
        },

      undoAIProposalReject:
        () =>
        ({ editor, tr, dispatch }) => {
          const entry = this.storage.undoStack.at(-1)
          if (!entry) {
            // Nothing of ours to undo — returning false lets the keymap chain
            // fall through to the UndoManager's own undo.
            return false
          }
          if (entry.kind === 'batch') {
            if (dispatch) {
              this.storage.undoStack.pop()
              undoBatch(entry, editor, tr)
            }
            return true
          }
          if (dispatch) {
            this.storage.undoStack.pop()
            this.storage.applying = true
            // Fused reject-and-apply: revert the document half FIRST so the
            // re-painted decoration resolves against the restored (unselected)
            // doc. The nested undo dispatches its own doc-changing transaction,
            // which would make tiptap's dispatch of THIS command's (stale) tr
            // throw "mismatched transaction" — suppress it; all work here rides
            // the nested dispatches. Optional-call: `undo` is registered by the
            // UndoManager extension (Liveblocks collab live, StarterKit
            // undoRedo elsewhere); absent on bare editors, where fused entries
            // are never recorded.
            if (entry.withDocUndo) {
              tr.setMeta('preventDispatch', true)
              editor.commands.undo?.()
            }
            // Re-paint the decoration; the proposal's stored from/to are
            // remapped against the current doc just like any other add.
            editor.commands.addAIProposals([entry.proposal])
            this.storage.applying = false
            this.options.onRepend?.(entry.proposal.id)
            this.storage.redoStack.push(entry)
          }
          return true
        },

      redoAIProposalReject:
        () =>
        ({ editor, tr, dispatch }) => {
          const entry = this.storage.redoStack.at(-1)
          if (!entry) {
            /* A bulk decision was undone and nothing has re-armed the note's
             * history since: swallow the key and do nothing. Falling through
             * here is what half-restored an "Accept all" — the collaboration
             * UndoManager still holds the sweep's narrative edits and would
             * replay them, while the questionnaire answers this extension's
             * `restore` reversed stay reversed.
             *
             * The swallow LASTS until a forward edit, rather than covering
             * only the next press. A one-shot would just move the fall-through
             * one key press later, and a clinician who taps Cmd+Shift+Z twice
             * would get the same half-restored sweep with no warning. A
             * forward edit is the right release because it is what discards
             * the UndoManager's own redo branch (a new tracked change empties
             * its redo stack) — the note's redo returns exactly when it can no
             * longer reach the sweep. Note that a further reject or another
             * bulk control does NOT release it: those change no content, so
             * the sweep is still sitting in the note's redo stack.
             *
             * The cost is that until the clinician types, Cmd+Shift+Z does
             * nothing at all — including for a pre-sweep note edit they undid
             * by pressing Cmd+Z past the sweep. Taking that over replaying a
             * sweep's prose without its answers: one is a key that does
             * nothing, the other is a note that contradicts the record with
             * nothing on screen saying so. */
            return this.storage.batchUndone
          }
          if (dispatch) {
            this.storage.redoStack.pop()
            this.storage.applying = true
            // Fused entry: re-apply the document half (the choice selection)
            // alongside the re-reject, mirroring the undo side (including the
            // stale-tr dispatch suppression).
            if (entry.withDocUndo) {
              tr.setMeta('preventDispatch', true)
              editor.commands.redo?.()
            }
            editor.commands.rejectAIProposal(entry.proposal.id)
            this.storage.applying = false
            this.options.onReReject?.(entry.proposal.id)
            this.storage.undoStack.push(entry)
          }
          return true
        },

      undoAIProposalBatch:
        () =>
        ({ editor, tr, dispatch }) => {
          /* The bar's button stands for one particular sweep, so it takes the
           * batch entry wherever it sits — a single reject made since would
           * otherwise be what Cmd+Z's top-of-stack rule reached first. There
           * is at most one: every sweep clears the stacks before arming. */
          const index = this.storage.undoStack.findIndex(
            (entry) => entry.kind === 'batch'
          )
          const entry = this.storage.undoStack[index]
          if (!entry || entry.kind !== 'batch') {
            return false
          }
          if (dispatch) {
            this.storage.undoStack.splice(index, 1)
            undoBatch(entry, editor, tr)
          }
          return true
        },

      clearAIProposalRejectStacks:
        () =>
        ({ dispatch }) => {
          // Called by Reject All so stale single-reject entries don't remain
          // poppable after a batch dismiss: Cmd+Z after Reject All delegates
          // cleanly to the doc UndoManager rather than re-pending one earlier
          // individually-rejected suggestion while the batch sits rejected.
          if (dispatch) {
            this.storage.undoStack = []
            this.storage.redoStack = []
            /* `batchUndone` deliberately survives: it tracks whether an undone
             * sweep is still sitting in the note's OWN redo stack, which
             * emptying ours says nothing about. Only a forward edit clears it. */
          }
          return true
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-z': () => this.editor.commands.undoAIProposalReject(),
      'Mod-Shift-z': () => this.editor.commands.redoAIProposalReject(),
      'Mod-y': () => this.editor.commands.redoAIProposalReject(),
    }
  },

  addProseMirrorPlugins() {
    const storage = this.storage
    return [
      new Plugin({
        key: AI_PROPOSAL_UNDO_PLUGIN_KEY,
        /* Disarm both stacks on a genuinely-LOCAL forward edit that isn't our
         * own re-pend/re-reject. A forward edit must make the prior reject
         * non-undoable (so Cmd+Z undoes the edit, not the suggestion), and it
         * also strands any redo history. AI-proposal decoration meta carries no
         * doc change, so painting/removing decorations never trips this. */
        appendTransaction(transactions) {
          if (storage.applying) {
            return null
          }
          if (
            storage.undoStack.length === 0 &&
            storage.redoStack.length === 0 &&
            !storage.batchUndone
          ) {
            return null
          }
          // Only a locally-typed forward edit disarms. A change that arrived
          // through Yjs — a remote peer editing concurrently, or an undo/redo —
          // must NOT disarm: otherwise a foreign edit interleaved between Cmd+Z
          // and Cmd+Shift+Z strands the redo half (the caveat-3 bug).
          // Decoration paint/remove transactions carry no doc change so they
          // never reach here; an accept's content insertion is a local forward
          // edit and correctly DOES disarm.
          const localEdit = transactions.some(isLocalForwardEdit)
          if (localEdit) {
            storage.undoStack = []
            storage.redoStack = []
            /* The edit gives the UndoManager a new tracked change, which
             * empties its redo stack — so the undone sweep is out of the
             * note's reach and Cmd+Shift+Z is safe to delegate again. */
            storage.batchUndone = false
          }
          return null
        },
      }),
    ]
  },
})

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aiProposalUndo: {
      /**
       * Record rejected proposals so Cmd+Z can re-pend them (LIFO). Call after
       * `rejectAIProposal`/`rejectAllAIProposals`, passing the proposals
       * captured from the plugin state before their decorations were removed.
       * Pass `withDocUndo` for a reject fused with a document change
       * (reject-and-apply): its undo/redo then also delegates a document
       * undo/redo so both halves travel as one step. Record AFTER the fused
       * transaction dispatches, or the disarm latch wipes the entry.
       */
      recordRejectedAIProposals: (
        proposals: AIProposal[],
        options?: { withDocUndo?: boolean }
      ) => ReturnType
      /**
       * Record a whole "Reject all" as ONE undoable entry, so a sweep of 20+
       * suggestions costs one Cmd+Z rather than one per suggestion. `restore`
       * is the host's undo for everything the sweep dismissed: it re-pends the
       * records (painted, never-placed, and questionnaire atoms alike) AND
       * repaints the decorations the sweep cleared, so nothing is left for this
       * extension to paint. Call it after
       * `clearAIProposalRejectStacks`, and clear the stacks again if the batch
       * is spent some other way (the inline undo button), or the key press
       * that follows would restore it a second time.
       */
      recordRejectedAIProposalBatch: (restore: () => void) => ReturnType
      /**
       * Record a whole "Accept all" as ONE undoable entry. `restore` is the
       * host's compensating write for everything the sweep decided outside
       * this editor — on the care plan, the questionnaire answers it patched
       * into the response. Pass `withDocUndo` when the sweep also changed the
       * document (it accepted at least one inline proposal), so one press
       * takes back both halves: the entry delegates the document undo to the
       * UndoManager before running `restore`. Record it AFTER the accepts have
       * applied, or the disarm latch wipes the entry.
       */
      recordAcceptedAIProposalBatch: (
        restore: () => void,
        options?: { withDocUndo?: boolean }
      ) => ReturnType
      /**
       * Re-pend the most recent un-disarmed reject (re-paint + onRepend), or
       * return false so the keymap chain falls through to the UndoManager.
       * Bound to Mod-z.
       */
      undoAIProposalReject: () => ReturnType
      /**
       * Re-reject the proposal a prior undoAIProposalReject restored, or return
       * false to fall through. Bound to Mod-Shift-z / Mod-y. Returns true
       * having done NOTHING when a bulk decision was undone and nothing has
       * re-armed the note's history since: a bulk decision has no redo, and
       * delegating the key would replay an accept sweep's narrative edits
       * without the questionnaire answers its undo reversed.
       */
      redoAIProposalReject: () => ReturnType
      /**
       * Take back the recorded bulk decision from wherever it sits in the
       * stack — the bar's "Undo … of all" button, which names one sweep rather
       * than whatever was decided last. Returns false when no sweep is armed
       * (none recorded, or a forward edit disarmed it).
       */
      undoAIProposalBatch: () => ReturnType
      /**
       * Clear both the undo and redo stacks. Called by Reject All so stale
       * single-reject entries don't remain poppable after a batch dismiss —
       * Cmd+Z then delegates to the doc UndoManager rather than partially
       * restoring one individually-rejected suggestion.
       */
      clearAIProposalRejectStacks: () => ReturnType
    }
  }
}
